import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, finalize, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { CustomHttpHeaderName } from '../types/http.types';
import { LoadingService } from '../features/shared/loading/loading.service';
import { ApiErrorCode, ErrorService } from '@services/error.service';
import { LocationService } from '@services/location.service';

@Injectable()
export class ResponseInterceptor implements HttpInterceptor {
  constructor(
    private loadingService: LoadingService,
    private readonly errorService: ErrorService,
    private readonly locationService: LocationService,
    private readonly router: Router,
  ) {}

  intercept(request: HttpRequest<object>, next: HttpHandler): Observable<HttpEvent<object>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status >= 400 && error.status !== 404) {
          this.captureHttpError(error, request);
        }

        if (this.redirectOnDateConflict(error)) {
          return EMPTY;
        }

        switch (error.status) {
          case 401:
            // #TODO handle if applicable
            break;
          case 500:
            // #TODO show snackbar (Internal Server Error)
            break;
          case 404:
            // #TODO show snackbar (Resource Not Found)
            break;
          case 403:
            // #TODO show snackbar (Forbidden Action)
            break;
        }

        // pass the error on to the next layer
        return throwError(() => error);
      }),
      finalize(() => {
        // update the global loading state
        const requestId = request.headers.get(CustomHttpHeaderName.REQUEST_ID);
        if (requestId) {
          this.loadingService.endAction(requestId);
        }
      }),
    );
  }

  /**
   * Stale item startTime: send user to select-date to re-pick.
   */
  private redirectOnDateConflict(error: HttpErrorResponse): boolean {
    if (this.errorService.extractApiErrorCode(error) !== ApiErrorCode.AppointmentDateConflict) {
      return false;
    }
    if (this.isOnScheduleScreen()) {
      return false;
    }
    void this.router.navigate(['/', this.locationService.getLocation().uuid, 'services', 'select-date'], {
      state: { dateConflict: true },
    });
    return true;
  }

  private isOnScheduleScreen(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.routeConfig?.path === 'select-date' || route.routeConfig?.path === 'reschedule';
  }

  private captureHttpError(error: HttpErrorResponse, request: HttpRequest<object>): void {
    const errorCode = this.errorService.extractApiErrorCode(error);
    const errorMessage = this.errorService.extractApiErrorMessages(error);
    const requestId = request.headers.get(CustomHttpHeaderName.REQUEST_ID);

    Sentry.withScope((scope) => {
      scope.setLevel('warning');

      scope.setTags({
        'http.status_code': String(error.status),
        'http.method': request.method,
        'api.error_code': errorCode ?? 'unknown_error',
      });

      if (requestId) scope.setTag('request_id', requestId);

      const bodyData = request.body ? JSON.parse(JSON.stringify(request.body)) : null;

      Sentry.addBreadcrumb({
        category: 'api.request',
        message: `Payload for ${request.method} ${request.url}`,
        data: { body: bodyData },
        level: 'info',
      });

      scope.setContext('request_details', {
        url: error.url ?? request.urlWithParams,
        body: bodyData,
      });

      scope.addEventProcessor((event) => {
        event.request = {
          url: error.url ?? request.urlWithParams,
          method: request.method,
          data: { bodyData },
        };
        return event;
      });

      scope.setContext('api_response', {
        errorCode,
        message: errorMessage,
        fullError: typeof error.error === 'object' ? JSON.stringify(error.error) : error.error,
      });

      const messageText = Array.isArray(errorMessage) ? errorMessage.join('; ') : errorMessage;
      const summary = messageText
        ? `HTTP ${error.status}: ${messageText}`
        : `HTTP ${error.status} ${error.statusText || 'Error'}${errorCode ? `: ${errorCode}` : ''}`;

      // Avoid having all request-originated errors in the same Sentry group
      scope.setFingerprint(['http-error', String(error.status), errorCode ?? 'unknown_error']);

      Sentry.captureException(new Error(summary));
    });
  }
}
